import { Folder, ModelUtils } from '../../models';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { FolderIcon } from '../Icons/FolderIcon';
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { CreateTemplatingItemButton } from '../Buttons/CreateTemplatingItemButton';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModuleActions } from '@lifeomic/chroma-react/components/TableModule/';
import { useStyles as useTableModuleStyles } from '@lifeomic/chroma-react/components/TableModule/TableModule';
import { useCommonStyles } from '../styles';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';

const useStyles = makeStyles(theme => ({
  folderButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    padding: theme.spacing(1),
    position: 'relative',
  },
  folderButton: {
    minWidth: 0,
    flexShrink: 1,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflow: 'hidden',
    padding: 0,
    border: 'none',
    background: 'none',
  },
  folderName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  folderButtonContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  createTemplatingItemButton: {
    flexShrink: 0,
    marginLeft: 8,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  tableModuleActions: {
    minWidth: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    // override the left: 0 from the tableModuleStyles which is high specificity
    left: 'unset!important',
    margin: theme.spacing(0.5),
  },
}));

type FolderButtonProps = {
  folder: Folder;
  className?: string;
  onClick?: () => void;
  onTemplatingItemCreate?: () => void;
  isExpanded?: boolean;
  folderNodeActions?: (data: Folder) => React.ReactNode;
};

export const FolderButton: React.FC<FolderButtonProps> = ({
  folder,
  className,
  onClick,
  isExpanded = false,
  folderNodeActions,
}) => {
  const styles = useStyles();
  const tableModuleStyles = useTableModuleStyles();
  const commonStyles = useCommonStyles();

  const actions = useMemo(() => {
    return folderNodeActions?.(folder);
  }, [folderNodeActions, folder]);

  const folderName = ModelUtils.getName(folder);

  return (
    <div
      className={clsx(
        styles.folderButtonContainer,
        tableModuleStyles.tableDataRow,
        commonStyles.fileSystemNode,
      )}
    >
      <Tooltip title={folderName} placement="right">
        <ButtonUnstyled
          onClick={onClick}
          className={clsx(styles.folderButton, className)}
        >
          <div className={styles.folderButtonContent}>
            <FolderIcon isExpanded={isExpanded} />
            <Text size="table" className={styles.folderName}>
              {folderName}
            </Text>
          </div>
        </ButtonUnstyled>
      </Tooltip>
      <TableModuleActions
        className={clsx(
          tableModuleStyles.tableModuleActions,
          styles.tableModuleActions,
        )}
      >
        {actions ? (
          actions
        ) : (
          <CreateTemplatingItemButton.Icon color="inverse" folder={folder} />
        )}
      </TableModuleActions>
    </div>
  );
};
